import {
  AssignmentsListingGetters,
  AssignmentsListingActions,
  AssignmentsListingMutations
} from '@/app/features/assignments/features/assignments-listing/assignments-listing.keys';
import { assignmentsListingService } from '@/app/features/assignments/features/assignments-listing/assignments-listing.service';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import i18n from '@/i18n';

export const getInitialState = () => {
  return {
    assignmentsLoading: true,
    assignments: [],
    assignmentsFilteredRecordCount: 0,
    assignmentsFilters: {
      dateRangeFilters: {
        startDateMin: null,
        startDateMax: null,
        stopDateMin: null,
        stopDateMax: null
      },
      statusFilters: {
        draft: false,
        submitted: false,
        approved: false,
        activeOnline: false,
        activeOffline: false,
        finished: false,
        rejected: false,
        obsolete: false
      },
      searchParam: ''
    },
    assignmentsSortOrder: {
      sortBy: 'payrollNumber',
      sortDirection: 'asc'
    },
    assignmentsPaginationParameters: {
      recordsPerPage: 10,
      offset: 0
    },
    assignmentsBulkAction: null,
    modifiedAssignments: {},
    dashboardSummary: [],
    executiveDashboardSummary: [],
    dashboardLoading: false,
    executiveDashboardLoading: false
  };
};

export const assignmentsListingStore = {
  state: getInitialState(),
  getters: {
    [AssignmentsListingGetters.AssignmentsList]: state => state.assignments,
    [AssignmentsListingGetters.AssignmentsFilters]: state => state.assignmentsFilters,
    [AssignmentsListingGetters.ModifiedAssignments]: state => state.modifiedAssignments,
    [AssignmentsListingGetters.SortBy]: state => state.assignmentsSortOrder.sortBy,
    [AssignmentsListingGetters.SortDirection]: state => state.assignmentsSortOrder.sortDirection,

    [AssignmentsListingGetters.AssignmentsPerPage]: state =>
      state.assignmentsPaginationParameters.recordsPerPage,

    [AssignmentsListingGetters.AssignmentsListingOffset]: state =>
      state.assignmentsPaginationParameters.offset,

    [AssignmentsListingGetters.AssignmentsFilteredRecordCount]: state =>
      state.assignmentsFilteredRecordCount,

    [AssignmentsListingGetters.SelectedAssignments]: state =>
      state.assignments.filter(assignment => assignment.isSelected),

    [AssignmentsListingGetters.AssignmentIsLoading]: state => state.assignmentsLoading,
    [AssignmentsListingGetters.DashboardSummary]: state => state.dashboardSummary,
    [AssignmentsListingGetters.IsDashboardLoading]: state => state.dashboardLoading,
    [AssignmentsListingGetters.ExecutiveDashboardSummary]: state => state.executiveDashboardSummary,
    [AssignmentsListingGetters.IsExecutiveDashboardLoading]: state =>
      state.executiveDashboardLoading
  },
  mutations: {
    [AssignmentsListingMutations.SetAssignmentsLoading](state, isLoading = true) {
      state.assignmentsLoading = isLoading;
    },
    [AssignmentsListingMutations.SetDashboardLoading](state, isLoading = true) {
      state.dashboardLoading = isLoading;
    },

    [AssignmentsListingMutations.SetAssignmentsList](state, assignments) {
      //add 'isSelected' field to assignment objects to support bulk actions
      const modifiedAssignments = assignments.map(assignment => {
        return { ...assignment, isSelected: false };
      });

      state.assignments = modifiedAssignments;
    },
    [AssignmentsListingMutations.SetAssignmentsFilters](state, filters) {
      state.assignmentsFilters = filters;
    },
    [AssignmentsListingMutations.SetAssignmentsSortOrder](state, sortOrder) {
      state.assignmentsSortOrder = sortOrder;
    },
    [AssignmentsListingMutations.SetAssignmentsPaginationParameters](state, pagination) {
      state.assignmentsPaginationParameters = pagination;
    },
    [AssignmentsListingMutations.ResetAssignmentsPaginationOffset](state) {
      state.assignmentsPaginationParameters.offset = 0;
    },
    [AssignmentsListingMutations.SetAssignmentsFilteredRecordCount](state, recordCount) {
      state.assignmentsFilteredRecordCount = recordCount;
    },
    [AssignmentsListingMutations.UpdateSelectedAssignments]: (state, payload) => {
      if (typeof payload === 'string') {
        const idx = state.assignments.findIndex(sheet => sheet.referenceNumber === payload);
        const selectedAssignment = state.assignments[idx];
        selectedAssignment.isSelected = !selectedAssignment.isSelected;
        state.assignments[idx] = selectedAssignment;
      } else if (typeof payload === 'object') {
        state.assignments.forEach(assignment => (assignment.isSelected = payload.checked));
      }
    },
    [AssignmentsListingMutations.SetAssignmentsBulkAction](state, bulkAction) {
      state.assignmentsBulkAction = bulkAction;
    },
    [AssignmentsListingMutations.SetAssignmentsSearchParam](state, searchParam) {
      state.assignmentsFilters.searchParam = searchParam;
    },
    [AssignmentsListingMutations.SetDashboardSummary]: (state, data) => {
      state.dashboardSummary = data;
    },
    [AssignmentsListingMutations.SetExecutiveDashboardSummary]: (state, data) => {
      state.executiveDashboardSummary = data;
    },
    [AssignmentsListingMutations.SetExecutiveDashboardLoading](state, isLoading = true) {
      state.assignmentsLoading = isLoading;
    }
  },
  actions: {
    [AssignmentsListingActions.RetrieveAssignments](
      { state, commit, dispatch, getters },
      options = null
    ) {
      commit(AssignmentsListingMutations.SetAssignmentsLoading);

      if (options && options.afterBulkAction) {
        const modifiedReferenceNumbers = getters[AssignmentsListingGetters.SelectedAssignments].map(
          assignment => assignment.referenceNumber
        );
        commit(AssignmentsListingMutations.SetModifiedAssignments, modifiedReferenceNumbers);
      }
      dispatch(AssignmentsListingActions.RetrieveAssignmentsFilteredRecordCount, options);

      return assignmentsListingService
        .retrieveAssignments(
          state.assignmentsFilters,
          state.assignmentsSortOrder,
          state.assignmentsPaginationParameters,
          options
        )
        .then(assignments => {
          commit(AssignmentsListingMutations.SetAssignmentsList, assignments);
        })
        .then(() => {
          commit(AssignmentsListingMutations.SetAssignmentsLoading, false);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('assignments.listing.errors.retrieveAssignments')
          });
          commit(AssignmentsListingMutations.SetAssignmentsLoading, false);
        });
    },

    [AssignmentsListingActions.RetrieveAssignmentsFilteredRecordCount]: function(
      { commit, state, dispatch },
      options = null
    ) {
      return assignmentsListingService
        .retrieveFilteredRecordCount(state.assignmentsFilters, options)
        .then(recordCount => {
          commit(AssignmentsListingMutations.SetAssignmentsFilteredRecordCount, recordCount);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('assignments.listing.errors.retrieveAssignments')
          });
          commit(AssignmentsListingMutations.SetAssignmentsLoading, false);
        });
    },

    [AssignmentsListingActions.SetAssignmentsFilters]({ commit, dispatch }, filters) {
      commit(AssignmentsListingMutations.SetAssignmentsFilters, filters);
      commit(AssignmentsListingMutations.ResetAssignmentsPaginationOffset);
      dispatch(AssignmentsListingActions.RetrieveAssignments);
    },
    [AssignmentsListingActions.SetAssignmentsSortOrder]({ commit, dispatch }, options) {
      commit(AssignmentsListingMutations.SetAssignmentsSortOrder, options.sortOrder);
      commit(AssignmentsListingMutations.ResetAssignmentsPaginationOffset);
      dispatch(AssignmentsListingActions.RetrieveAssignments, options);
    },
    [AssignmentsListingActions.SelectAssignment]({ commit }, referenceNumber) {
      commit(AssignmentsListingMutations.UpdateSelectedAssignments, referenceNumber);
    },
    [AssignmentsListingActions.SelectDeselectAllAssignments]: ({ commit }, isSelected) => {
      commit(AssignmentsListingMutations.UpdateSelectedAssignments, isSelected);
    },
    [AssignmentsListingActions.SetAssignmentsPaginationParameters]({ commit, dispatch }, options) {
      commit(AssignmentsListingMutations.SetAssignmentsPaginationParameters, options.event);
      dispatch(AssignmentsListingActions.RetrieveAssignments, options);
    },

    [AssignmentsListingActions.SetAssignmentsBulkAction]({ commit }, bulkAction) {
      commit(AssignmentsListingMutations.SetAssignmentsBulkAction, bulkAction);
    },

    [AssignmentsListingActions.DoAssignmentsBulkAction]({ commit, dispatch, getters }, bulkAction) {
      const selectedAssignmentRefs = getters[AssignmentsListingGetters.SelectedAssignments].map(
        assignment => assignment.referenceNumber
      );
      return assignmentsListingService
        .bulkUpdateAssignments(bulkAction, selectedAssignmentRefs)
        .then(() => {
          dispatch(AssignmentsListingActions.RetrieveAssignments, {
            afterBulkAction: true
          });
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('assignments.listing.errors.bulkApproveRejectFailed')
          });
          commit(AssignmentsListingMutations.SetAssignmentsLoading, false);
        });
    },

    [AssignmentsListingActions.ExportAssignmentsAsExcel]: function({ state }) {
      return assignmentsListingService.downloadAssignmentsXlsx(
        state.assignmentsFilters,
        state.assignmentsSortOrder
      );
    },

    [AssignmentsListingActions.UpdateAssignmentsList]({ commit, dispatch }, filterParams) {
      commit(
        AssignmentsListingMutations.SetAssignmentsSearchParam,
        filterParams.results.searchParam
      );
      if (filterParams.results.searchParam) {
        commit(AssignmentsListingMutations.ResetAssignmentsPaginationOffset);
        commit(AssignmentsListingMutations.SetAssignmentsList, filterParams.results.data);
        dispatch(AssignmentsListingActions.RetrieveAssignmentsFilteredRecordCount, null);
      } else {
        dispatch(AssignmentsListingActions.RetrieveAssignments, filterParams.options);
      }
    },
    [AssignmentsListingActions.SetAssignmentsLoading]: function({ commit }, flag) {
      commit(AssignmentsListingMutations.SetAssignmentsLoading, flag);
    },
    [AssignmentsListingActions.RetrieveDashboardSummary]: ({ commit }, roles) => {
      commit(AssignmentsListingMutations.SetDashboardLoading);
      let dashboardType;
      if (roles.includes('System Admin') || roles.includes('Senior Accounting')) {
        dashboardType = assignmentsListingService.getDashboardTotalSummary();
      } else if (roles.includes('Recruiter') || roles.includes('Account Manager')) {
        dashboardType = assignmentsListingService.getDashboardPersonSummary();
      } else if (roles.includes('Branch Admin')) {
        dashboardType = assignmentsListingService.getDashboardBranchSummary();
      }

      return dashboardType
        .then(data => {
          commit(AssignmentsListingMutations.SetDashboardSummary, data);
        })
        .then(() => {
          commit(AssignmentsListingMutations.SetDashboardLoading, false);
        });
    },
    [AssignmentsListingActions.RetrieveExecutiveDashboardSummary]: ({ commit }, roles) => {
      commit(AssignmentsListingMutations.SetExecutiveDashboardLoading);
      let dashboardType;
      if (roles.includes('System Admin') || roles.includes('Senior Accounting')) {
        dashboardType = assignmentsListingService.getExecutiveDashboardBranchSummary();
      }

      return dashboardType
        .then(data => {
          commit(AssignmentsListingMutations.SetExecutiveDashboardSummary, data);
        })
        .then(() => {
          commit(AssignmentsListingMutations.SetExecutiveDashboardLoading, false);
        });
    }
  }
};
